import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  startSocialListeningBrandAnalysis,
  startSocialListeningCompetitiveAnalysis,
  type SocialListeningStartResponse,
} from "src/services/SocialListeningAPIs";
import { getThrownApiDetail } from "src/utils/showApiDetailToast";
import "../Styles/Loader.scss";
import "../Styles/Shared.scss";
import StatusMessage from "../Components/StatusMessage";
import type { AnalysisType, BrandFormRow } from "../Types/SocialListeningTypes";
import { formRowToCompany, parseLoaderState } from "../Utils/FormMapping";
import {
  getStoredId,
  MAX_COMPETITORS,
  MIN_COMPETITORS,
  SOCIAL_LISTENING_ROUTES,
} from "../Utils/SocialListeningConstants";

const LOADER_KEY = "socialListingAgent.loader";

const hasBrandName = (row?: BrandFormRow) => Boolean(row?.brandName?.trim());

const SocialListingLoader: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const state = useMemo(() => parseLoaderState(location.state), [location.state]);
  const hasStartedRef = useRef(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const analysisType = useMemo<AnalysisType | null>(() => {
    if (state.analysisType) {return state.analysisType;}
    if (state.competitors?.length) {return "competitive_analysis";}
    if (state.brand) {return "brand_analysis";}
    return null;
  }, [state.analysisType, state.brand, state.competitors]);
  const formRoute =
    analysisType === "competitive_analysis"
      ? SOCIAL_LISTENING_ROUTES.competitiveAnalysis
      : SOCIAL_LISTENING_ROUTES.brandAnalysis;

  useEffect(() => {
    if (hasStartedRef.current) {return;}
    const redirect = (path: string) => navigate(path, { replace: true });
    const userId = getStoredId("user_id");
    const tenantId = getStoredId("tenant_id");

    if (analysisType === null) {
      redirect(SOCIAL_LISTENING_ROUTES.home);
      return;
    }

    let request: Promise<SocialListeningStartResponse>;
    if (analysisType === "competitive_analysis") {
      const competitors = state.competitors ?? [];
      const isValid =
        userId !== null &&
        tenantId !== null &&
        competitors.length >= MIN_COMPETITORS &&
        competitors.length <= MAX_COMPETITORS &&
        competitors.every(hasBrandName);
      if (!isValid) {
        redirect(SOCIAL_LISTENING_ROUTES.competitiveAnalysis);
        return;
      }
      request = startSocialListeningCompetitiveAnalysis({
        user_id: userId,
        tenant_id: tenantId,
        companies: competitors.map(formRowToCompany),
      });
    } else {
      if (userId === null || tenantId === null || !state.brand || !hasBrandName(state.brand)) {
        redirect(SOCIAL_LISTENING_ROUTES.brandAnalysis);
        return;
      }
      request = startSocialListeningBrandAnalysis({
        user_id: userId,
        tenant_id: tenantId,
        ...formRowToCompany(state.brand),
      });
    }

    hasStartedRef.current = true;
    request.catch((error: unknown) => {
      setErrorMessage(
        getThrownApiDetail(error) ||
          t(`${LOADER_KEY}.startFailed`, "We couldn't start this analysis. Please try again.")
      );
    });
  }, [analysisType, navigate, state.brand, state.competitors, t]);

  if (errorMessage) {
    return (
      <section className="social-listing-loader">
        <StatusMessage
          tone="error"
          title={t(`${LOADER_KEY}.startFailedTitle`, "Analysis not started")}
          message={errorMessage}
          action={{
            label: t(`${LOADER_KEY}.backToForm`, "Back to the form"),
            onClick: () => navigate(formRoute, { replace: true }),
          }}
        />
      </section>
    );
  }

  const subtitle =
    analysisType === "competitive_analysis"
      ? "Analyzing competitors and gathering insights... Good things are coming."
      : "Analyzing your brand and gathering insights... Good things are coming.";

  return (
    <section className="social-listing-loader">
      <div className="social-listing-loader__card">
        <h1 className="social-listing-loader__title">
          Hang tight! We're on it
          <span className="social-listing-loader__spinner" aria-hidden="true" />
        </h1>
        <p className="social-listing-loader__subtitle">
          {subtitle}
        </p>
        <p className="social-listing-loader__note">
          We'll email you as soon as your report is ready.
        </p>

        <img
          className="social-listing-loader__animation"
          src="/Icons/Business chart.gif"
          alt="Report analysis in progress"
        />

        <button
          type="button"
          className="button button--primary social-listing-loader__button"
          onClick={() => navigate(SOCIAL_LISTENING_ROUTES.history)}
        >
          View all Reports
        </button>
      </div>
    </section>
  );
};

export default SocialListingLoader;
